/**
 * A node in the resulting tree structure, grouping items under a common name.
 */
export interface NamedTreeNode<T> {
  name: string;
  items: T[];
}

/**
 * Buckets a flat array of items into named groups, sorted alphabetically.
 *
 * Groups are sorted by name; items within each group are sorted by `sortBy`.
 *
 * @param items        The items to group.
 * @param groupBy      Returns the group name for an item. Nullish values fall back to `defaultGroup`.
 * @param sortBy       Returns the sort key for an item within its group. Nullish values sort first.
 */
export function flatToNamedTree<T>(
  items: readonly T[],
  groupBy: (item: T) => string,
  sortBy: (item: T) => string
): NamedTreeNode<T>[] {
  const sorted = [...items].sort((a, b) => sortBy(a).localeCompare(sortBy(b)));

  const groups = new Map<string, T[]>();
  for (const item of sorted) {
    const name = groupBy(item);
    let bucket = groups.get(name);
    if (!bucket) {
      bucket = [];
      groups.set(name, bucket);
    }
    bucket.push(item);
  }
  return [...groups]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([name, items]) => ({ name, items }));
}
